import { ControlsProps } from "@/utils/types";
import { getValue, getValueKey } from "@/utils/helpers";
import { getBucketByYear } from "@/utils/selectors";
import { MAP_LINE_HOVER_WIDTH, MAP_LINE_WIDTH, MAP_MAX_ZOOM, MAP_MIN_ZOOM, MapParamsProps, MAP_VERSION, MAPBOX_USERNAME } from "./constants";

/** Get all Mapbox source IDs */
export const getSourceIds = (controls: ControlsProps) => [
	"state",
	`${controls.geo}-${controls.view}-${getBucketByYear(controls.year)}`
]
/**Get all Mapbox source URIs */
export const getSourceUris = (controls: ControlsProps) =>
	getSourceIds(controls).map(id =>
		`mapbox://${MAPBOX_USERNAME}.${id}?_cb=${MAP_VERSION}`
	);
/** Gets source ID */
export const getSourceId = (params: ControlsProps | MapParamsProps) =>
	`${params.geo}-${params.view}-${getBucketByYear(params.year)}`
/** Gets source layer ID */
export const getSourceLayerId = (params: ControlsProps | MapParamsProps) =>
	`${params.geo}-${params.view}-${getBucketByYear(params.year)}`
/** Gets layer ID based on active geography type (i.e. county) and provided layer type (i.e. fill) */
export const getLayerId = (params: ControlsProps | MapParamsProps, type: string) =>
	`${params.geo}-${type}`
/** Map > Choropleth */
/** Gets the name of property used for the choropleth shading */
export const getColorProp = (params: MapParamsProps) =>
	getValueKey(params as ControlsProps)
/** Map > Layers */
/** Gets all map layers in object with layer ID as the key and props as the value */
export const getMapLayers = (params: MapParamsProps) => ({
	[getLayerId(params, "line-hover")]: getLineActiveLayerProps(params),
	[getLayerId(params, "line")]: getLineLayerProps(params),
	[getLayerId(params, "fill")]: getFillLayerProps(params),
	[getLayerId(params, "fill-pattern")]: getFillPatternLayerProps(params),
});
/** Gets a single layer's properties by ID */
export const getMapLayer = (id: string, params: MapParamsProps) => {
	return getValue(id, getMapLayers(params))
};
/** Map > Layers > Fill */
/** Gets layer properties for fill layer */
const getFillLayerProps = (params: MapParamsProps) => ({
	id: getLayerId(params, "fill"),
	type: "fill",
	source: getSourceId(params),
	"source-layer": getSourceLayerId(params),
	paint: getFillLayerPaint(params),
	/** filter: getFilter(params), */
});
/** Map > Layers > Fill > Paint */
/** Gets paint properties for fill layer */
const getFillLayerPaint = (params: MapParamsProps) => {
	return {
		"fill-color": params?.extents.length && params?.colors.length ? [
			"case",
			["has", getColorProp(params)],
			[
				"case",
				["<", ["get", getColorProp(params)], params?.extents[4]],
				[
					"interpolate",
					["linear"],
					["get", getColorProp(params)],
					...params?.extents.map((value, index) =>
						[value, params?.colors[index] || "transparent"]
					).flat()
				],
				params?.colors[4],
			],
			"transparent",
		] : "transparent",
		"fill-opacity": [
			"interpolate",
			["linear"],
			["zoom"],
			MAP_MIN_ZOOM,
			1,
			MAP_MAX_ZOOM,
			0.75
		],
	};
};
/** Map > Layers > Fill > Pattern */
/** Gets layer properties for fill pattern layer */
const getFillPatternLayerProps = (params: MapParamsProps) => ({
	id: getLayerId(params, "fill-pattern"),
	type: "fill",
	source: getSourceId(params),
	"source-layer": getSourceLayerId(params),
	paint: getFillPatternLayerPaint(params),
});
/** Map > Layers > Fill > Pattern > Paint */
/** Gets paint properties for fill pattern layer */
const getFillPatternLayerPaint = (params: MapParamsProps) => {
	return {
		"fill-pattern": [
			"case",
			["has", getColorProp(params)],
			"",
			[
				"coalesce",
				["image", "pattern"],
			]
		],
		"fill-opacity": [
			"interpolate",
			["linear"],
			["zoom"],
			MAP_MIN_ZOOM,
			1,
			MAP_MAX_ZOOM,
			0.75
		],
	};
};
/** Map > Layers > Line */
/** Gets layer properties for line layer */
const getLineLayerProps = (params: MapParamsProps) => ({
	id: getLayerId(params, "line"),
	type: "line",
	source: getSourceId(params),
	"source-layer": getSourceLayerId(params),
	paint: getLineLayerPaint(params),
});
/** Map > Layers > Line > Props */
/** Gets paint properties for line layer */
export const getLineLayerPaint = (params: MapParamsProps) => ({
	"line-width": [
		"interpolate",
		["linear"],
		["zoom"],
		MAP_MIN_ZOOM,
		MAP_LINE_WIDTH,
		MAP_MAX_ZOOM,
		MAP_LINE_WIDTH * 2
	],
	"line-color": [
		"case",
		["has", getColorProp(params)],
		"rgba(229,231,235,0.4)",
		"rgba(229,231,235,0.2)",
	],
});
/** Map > Layers > Line Hover */
/** Gets layer properties for line hover layer */
const getLineActiveLayerProps = (params: MapParamsProps) => ({
	id: getLayerId(params, "line-active"),
	type: "line",
	source: getSourceId(params),
	"source-layer": getSourceLayerId(params),
	paint: getLineActiveLayerPaint(params),
	/** filter: getFilter(params), */
});
/** Map > Layers > Line Hover > Props */
/** Gets paint properties for line hover layer */
export const getLineActiveLayerPaint = (_params: MapParamsProps) => ({
	"line-width": [
		"interpolate",
		["linear"],
		["zoom"],
		MAP_MIN_ZOOM,
		MAP_LINE_HOVER_WIDTH,
		MAP_MAX_ZOOM,
		MAP_LINE_HOVER_WIDTH * 2
	],
	"line-color": [
		"case",
		["==", ["feature-state", "hovered"], true],
		"#F8FE83",
		["==", ["feature-state", "selected"], true],
		"#F8FE83",
		"transparent"
	],
	"line-opacity": [
		"case",
		["==", ["feature-state", "hovered"], true],
		1,
		["==", ["feature-state", "selected"], true],
		1,
		0
	],
});
/** Map > Layers > State */
export const getMapStaticLayer = (geo: string) => ({
	id: `${geo}-line`,
	type: "line",
	source: `${geo}`,
	"source-layer": geo,
	paint: {
		"line-color": "rgba(229,231,235,0.5)",
		"line-width": [
			"interpolate",
			["linear"],
			["zoom"],
			MAP_MIN_ZOOM,
			MAP_LINE_WIDTH * 2,
			MAP_MAX_ZOOM,
			MAP_LINE_WIDTH * 4
		],
	},
});